import type { TiCallListData, TiOpportunityData, TiOutcome } from './types';

/** Opportunity tab demo (board 12). Both columns of the funnel reconcile exactly. */
export const TI_OPPORTUNITY: TiOpportunityData = {
  eyebrow: '4,000 active patients · 2,812 with treatment diagnosed and not scheduled',
  titleLead: 'Treatment',
  titleAccent: 'Intelligence',
  subtitle:
    'What is already diagnosed, what it is worth, and which of it is worth an hour of somebody’s day.',
  funnel: [
    { key: 'gross', label: 'Diagnosed, not scheduled', patients: 2812, value: 1842000, kind: 'gross' },
    { key: 'unreachable', label: 'Inactive, moved, or no way to reach them', note: 'Two years since a visit, or the phone and email both bounce.', patients: 902, value: -586000, kind: 'deduction', barTone: 'unreachable' },
    { key: 'over-year', label: 'Diagnosed over a year ago', note: 'Books at 3%. Handed to Recall as a reactivation, not chased as a plan.', patients: 1088, value: -664000, kind: 'deduction', barTone: 'over-year' },
    { key: 'booked', label: 'Already booked, or in treatment', note: 'Nothing to do. Counting them is how a number becomes a fantasy.', patients: 284, value: -142000, kind: 'deduction', barTone: 'booked' },
    { key: 'held', label: 'Held by the practice', note: 'Past-due balance, insurance dispute, or a clinical hold from the doctor.', patients: 226, value: -138000, kind: 'deduction', barTone: 'held' },
    { key: 'reachable', label: 'Reachable now', patients: 312, value: 312000, kind: 'total', barTone: 'reachable' },
  ],
  reachableAvg: 'Average case $1,000',
  stackedNote: '17% of the money is workable today.',
  sideCards: [
    { key: 'scheduled', label: 'Scheduled in 30 days', value: '$28,400', sub: 'by the agent, from 34 patients', linkLabel: 'AI Team › Treatment Coordinator' },
    { key: 'human-call', label: 'When a person calls', value: '38%', sub: 'book — 29 calls, 11 cases, $14,200', note: 'Half the money on half the contacts. The constraint is hours, not leads.' },
    { key: 'diagnosed-month', label: 'Diagnosed this month', value: '$52,400', sub: '61 new cases, 47 patients', note: 'More came in than went out. The backlog grew by $24,000 this month.' },
  ],
  caseBands: [
    { key: 'b1', band: 'Under $300', patients: 96, total: '$19,400', booksPct: '41%', expected: '$8.0k', whatItTakes: 'One text with the estimate. A yes on the phone in three minutes.' },
    { key: 'b2', band: '$300 – $800', patients: 88, total: '$46,200', booksPct: '33%', expected: '$15.2k', whatItTakes: 'Text first, a call if there is no reply by day three.' },
    { key: 'b3', band: '$800 – $1,500', patients: 62, total: '$71,800', booksPct: '22%', expected: '$15.8k', whatItTakes: 'A call. Estimate and insurance in writing before it.' },
    { key: 'b4', band: '$1,500 – $3,000', patients: 41, total: '$89,100', booksPct: '14%', expected: '$12.5k', whatItTakes: 'A call, then a consult. Often needs financing.' },
    { key: 'b5', band: 'Over $3,000', patients: 25, total: '$85,500', booksPct: '8%', expected: '$6.8k', whatItTakes: 'A consult with the doctor. Usually phased across two plan years.' },
  ],
  caseBandsPunch:
    'The 184 patients under $800 are worth $65,600 and book at better than one in three — $23,200 expected. The 25 patients over $3,000 are worth $85,500 and book at one in twelve — $6,800 expected. A list sorted by case value puts the wrong twenty-five names at the top.',
  providers: [
    { key: 'saito', label: 'Dr. Saito', sub: '94 patients', value: '$118,400', amount: 118400 },
    { key: 'chen', label: 'Dr. Chen', sub: '81 patients', value: '$94,200', amount: 94200 },
    { key: 'okafor', label: 'Dr. Okafor', sub: '62 patients', value: '$61,100', amount: 61100 },
    { key: 'hygiene', label: 'Hygiene — perio', sub: '75 patients', value: '$38,300', amount: 38300 },
  ],
  providerNote:
    'Not a scoreboard. A doctor with a large column is diagnosing thoroughly; the gap is in the handoff after the chair.',
  procedures: [
    { key: 'crown', label: 'Crown & onlay', value: '$104,300', amount: 104300 },
    { key: 'perio', label: 'Perio — SRP and maintenance', value: '$58,100', amount: 58100 },
    { key: 'implant', label: 'Implant — single', value: '$52,400', amount: 52400 },
    { key: 'root-canal', label: 'Root canal', value: '$44,200', amount: 44200 },
    { key: 'fillings', label: 'Fillings', value: '$31,600', amount: 31600 },
    { key: 'extraction', label: 'Extraction & surgical', value: '$21,400', amount: 21400 },
  ],
};

/** Call list tab demo (board 13). Eleven signals from nine patients, overnight. */
export const TI_CALL_LIST: TiCallListData = {
  window: 'Tue 5:31pm → Wed 8:00am · 11 signals from 9 patients · 4 logged today',
  titleLead: 'Treatment',
  titleAccent: 'Intelligence',
  subtitle: 'Patients who did something last night. This list arrives overnight, and it empties.',
  signalsCount: 11,
  patientsCount: 9,
  discardedNote: '2 opens discarded — link previews',
  whyYours:
    'Each one is yours until the end of tomorrow. If nobody calls, the agent picks the thread back up.',
  summary: [
    { tier: 'talking', label: 'Talking to the agent', count: 3 },
    { tier: 'booking', label: 'Started booking, didn’t finish', count: 1 },
    { tier: 'viewed', label: 'Viewed their x-ray', count: 2 },
    { tier: 'opened', label: 'Opened their plan', count: 6 },
  ],
  listWorth: '',
  groups: [
    {
      tier: 'talking',
      title: 'Talking to the agent about their case',
      note: 'The strongest signal there is. They typed back about their own treatment.',
      count: 3,
      rows: [
        {
          // Todd Baird, p002 — the same patient as the cheat sheet (fix 29 §3.3).
          id: 'amara-reza',
          tier: 'talking',
          turns: 4,
          name: 'Todd Baird',
          meta: '52 · Dr. Carter · Sun Life PPO',
          caseText: '$2,130 · crown #14 + deep cleaning',
          caseSub: 'Diagnosed 11 days ago · $1,240 of benefit left · yours until Thu 6:00pm',
          phone: '(415) 555-0142',
          expanded: true,
          transcript: [
            { who: 'Todd', text: 'how much would it be with my insurance?', at: '8:38pm' },
            { who: 'ELVA', text: 'Good question — your plan page shows what your insurance covers: alamoheights.elva.ai/p/K7q9… Someone from our team will call you tomorrow.', at: '8:38pm', isElva: true },
            { who: 'Todd', text: 'and could it wait until january? my benefit resets then', at: '9:02pm' },
          ],
          openWith:
            'his second question. He is deciding between now and the new plan year — and waiting costs him the $1,240 he has left this year.',
        },
        {
          id: 'marcus',
          tier: 'talking',
          turns: 6,
          name: 'Marcus Whitfield',
          meta: '44 · Dr. Okafor · self-pay',
          caseText: '$4,600 · implant #19',
          caseSub: 'Diagnosed 24 days ago · yours until Thu 6:00pm',
          phone: '(210) 555-0631',
          collapsedLabel: '6 messages · cheat sheet',
        },
        {
          id: 'priya',
          tier: 'talking',
          turns: 2,
          name: 'Priya Raman',
          meta: '37 · Dr. Chen · Aetna',
          caseText: '$920 · onlay #3',
          caseSub: 'Diagnosed 41 days ago · yours until Thu 6:00pm',
          phone: '(210) 555-0455',
          collapsedLabel: '2 messages · cheat sheet',
        },
      ],
    },
    {
      tier: 'booking',
      title: 'Started booking and didn’t finish',
      note: 'They tapped Book, then stopped before choosing a time. A nudge away from done.',
      count: 1,
      rows: [
        { id: 'delphine', count: 1, tier: 'booking', name: 'Delphine Osei', meta: '47 · Dr. Saito · Cigna', caseText: '$1,240 · crown #19', caseSub: 'Diagnosed 16 days ago', phone: '(210) 555-0642', signalLine: 'Started booking, didn’t finish · last night 9:12pm' },
      ],
    },
    {
      tier: 'viewed',
      title: 'Viewed their x-ray',
      note: 'Not idle. Somebody trying to understand what is wrong with them.',
      count: 2,
      rows: [
        { id: 'alma', count: 2, tier: 'viewed', name: 'Alma Reyes', meta: '58 · Dr. Saito · MetLife', caseText: '$340 · two fillings', caseSub: 'Diagnosed 9 days ago · $620 of benefit left', phone: '(210) 555-0277', signalLine: 'Viewed their x-ray twice · last night 8:41pm' },
        { id: 'gordon', count: 1, tier: 'viewed', name: 'Gordon Vance', meta: '61 · hygiene · Delta PPO', caseText: '$2,750 · perio, four quadrants', caseSub: 'Diagnosed 63 days ago', phone: '(210) 555-0102', signalLine: 'Viewed their x-ray · last night 10:12pm' },
      ],
    },
    {
      tier: 'opened',
      title: 'Opened their plan page',
      note: 'Interest, unresolved. Two or more opens — a single one is not evidence.',
      count: 4,
      rows: [
        { id: 'renee', count: 3, tier: 'opened', name: 'Renee Salcido', meta: '31 · Dr. Chen · Cigna', caseText: '$280 · filling #30', caseSub: 'Diagnosed 6 days ago', phone: '(210) 555-0913', signalLine: 'Opened their plan 3 times · last night 7:20pm → 9:58pm' },
        { id: 'callie', count: 2, tier: 'opened', name: 'Callie Nguyen', meta: '29 · hygiene · Guardian', caseText: '$190 · sealants', caseSub: 'Diagnosed 14 days ago', phone: '(210) 555-0790', signalLine: 'Opened their plan twice · last night 9:04pm' },
        { id: 'hector', count: 2, tier: 'opened', name: 'Hector Barrientos', meta: '52 · Dr. Okafor · Delta PPO', caseText: '$1,640 · root canal + crown', caseSub: 'Diagnosed 19 days ago', phone: '(210) 555-0384', signalLine: 'Opened their plan twice · yesterday 6:47pm' },
        { id: 'junie', count: 2, tier: 'opened', name: 'Junie Park', meta: '40 · Dr. Saito · Aetna', caseText: '$620 · onlay #19', caseSub: 'Diagnosed 33 days ago', phone: '(210) 555-0561', signalLine: 'Opened their plan twice · yesterday 5:58pm' },
      ],
    },
  ],
};

/** The eight call outcomes, in three groups (board 14, §4.9). */
export const TI_OUTCOMES: TiOutcome[] = [
  { key: 'booked', group: 'reached', label: 'Booked', consequence: 'The case closes. Reminders picks the appointment up.' },
  { key: 'think', group: 'reached', label: 'Wants to think about it', consequence: 'The campaign waits 30 days.' },
  { key: 'declined', group: 'reached', label: 'Declined the treatment', consequence: 'Counts as a no. A second no ends the campaign.' },
  { key: 'call-back', group: 'reached', label: 'Call back on…', consequence: 'Every channel stops until that date, then resumes.', needsDate: true },
  { key: 'voicemail', group: 'not-reached', label: 'Left a voicemail', consequence: 'Logged as an attempt, not a contact. The hold runs on.' },
  { key: 'no-answer', group: 'not-reached', label: 'No answer', consequence: 'Logged as an attempt, not a contact.' },
  { key: 'wrong-number', group: 'not-reached', label: 'Wrong number', consequence: 'The number comes off their record and they move to unreachable.', destructive: true },
  { key: 'do-not-contact', group: 'permanent', label: 'Do not contact about this again', consequence: 'Permanent, on this case, on every channel. Topic-level, not a global opt-out.', destructive: true },
];
